/**
 * RitualDetailPage
 * Main page for viewing ritual details. Supports all ritual formats:
 * - Self-guided ritual
 * - Self-guided course (enrolled/not enrolled)
 * - In-person ritual
 * - Live virtual ritual
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  Archive,
  ArchiveRestore,
  Bookmark,
  EllipsisVertical,
  Flag,
  Pencil,
  Share,
} from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import aiAvatarImg from "@/assets/icons/ai-conversation-avatar.png"
import { GuestAction } from "@/components/guest"
import { ArrowLeftIcon } from "@/components/icons"
import { ReportModal } from "@/components/shared/ReportModal"
import { ShareModal } from "@/components/shared/ShareModal"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { LoginModal } from "@/features/auth/components/LoginModal"
import { useAuthStore } from "@/features/auth/store/authStore"
import { consumeDeepLinkLanding } from "@/features/auth/utils/authReturnTo"
import { ProfileIncompleteModal } from "@/features/profile/components/ProfileIncompleteModal"
import { useToggleRitualSave } from "@/features/profile/hooks/useSavedRituals"
import { isProfileFullyCompleted } from "@/features/profile/utils/profileCompletion"
import { RitualTourOverlay } from "@/features/tour/components/RitualTourOverlay"
import { useTourInit } from "@/features/tour/hooks/useTourInit"
import { getDemoRitualId, getTourStatus } from "@/features/tour/services/tour.service"
import { useIsMobile } from "@/hooks/useIsMobile"
import { useTranslation } from "@/language"
import { CancelRitualDialog } from "../components/CancelRitualDialog"
import { CourseModulesList } from "../components/CourseModulesList"
import { DiscussionSection } from "../components/detail/DiscussionSection"
import { ExploreMoreSection } from "../components/detail/ExploreMoreSection"
import {
  IntentionSection,
  StepByStepGuidanceSection,
  WhatYoullExperienceSection,
  WhatYoullNeedSection,
} from "../components/detail/RitualAccordionSections"
import { RitualCourseDays } from "../components/detail/RitualCourseDays"
import { RitualDetailAdminBar } from "../components/detail/RitualDetailAdminBar"
import { RitualInfoSection } from "../components/detail/RitualInfoSection"
import { RitualMediaCarousel } from "../components/detail/RitualMediaCarousel"
import { RitualSidebar } from "../components/detail/RitualSidebar"
import { RitualTagsList } from "../components/detail/RitualTagsList"
import { useRitualOrganizers } from "../hooks/useRitualOrganizers"
import {
  adminCompleteRitual,
  archiveRitual,
  getRitualById,
  publishRitual,
  transformRitualDetailToUI,
  unarchiveRitual,
} from "../services/ritual.service"
import type { RitualDetailData, RitualOrganizer } from "../types/detail.types"

// ============================================================================
// Button State Helper
// ============================================================================

/**
 * Simple helper that computes button state from API response.
 * No complex logic - just derives UI state from backend data.
 *
 * Button behavior by format:
 * - In-Person/Virtual-Course: "JOIN" → "JOINED" + hamburger
 * - Self-guided: "JOIN" → "START RITUAL" + hamburger
 * - Self-guided-course: "JOIN" → "CONTINUE COURSE" + hamburger
 * - Paid rituals: "Buy - $X" → (format-dependent)
 */
function getRitualButtonState(
  ritual: RitualDetailData,
  currentUserId?: string | null,
  t?: (key: string, options?: Record<string, unknown>) => string,
) {
  const isCreator = ritual.createdBy === currentUserId
  const isOrganizer = ritual.isOrganizer
  const isMember = ritual.membershipDetails?.isMember
  const canAccess = ritual.membershipDetails?.canAccessContent
  const isJoined = isOrganizer || isMember
  const isPaid = ritual.isPaid

  // For non-admin users (non-creator, non-organizer), canceled/completed rituals have no action buttons
  // This includes members who joined but are not organizers
  const isNonAdmin = !isCreator && !isOrganizer
  // Hide action buttons if ritual is canceled/completed AND user is not the owner/organizer
  // (i.e., user is either a non-organizer member, or just a visitor/non-member)
  if ((ritual.canceled || ritual.completed) && isNonAdmin) {
    return {
      showActionButton: false,
      actionLabel: ritual.canceled ? "Canceled" : "Completed",
      showHamburger: false,
      showStartButton: false,
      buttonVariant: "secondary" as const,
      canAccessContent: canAccess ?? false,
      canStartRitual: false,
      isInProgress: false,
      completedDays: 0,
      courseProgress: undefined,
      isInviteReq: false,
      hasPendingRequest: false,
      isInvited: false,
      inviteId: undefined,
    }
  }

  // Self-guided ritual specific state
  const currentProgressStatus = ritual.currentProgressStatus
  const completedDays = ritual.completedDays ?? 0
  const canStartRitual = ritual.canStartRitual ?? true

  // Course progress specific state
  const courseProgress = ritual.courseProgress
  // Use backend-provided flags - showContinueCourse if actively doing course
  const showContinueCourse = courseProgress?.showContinueCourse ?? false

  // Determine button configuration based on format and membership
  let actionLabel: string
  let showHamburger = false
  let showStartButton = false
  let buttonVariant: "primary" | "secondary" = "secondary"

  const isInvited = ritual.membershipDetails?.isInvited ?? false
  const inviteId = ritual.membershipDetails?.memberDetails?.id ?? undefined

  if (!isJoined) {
    // Not joined - show JOIN, BUY, REQUEST TO JOIN, or ACCEPT INVITE button
    if (isPaid) {
      actionLabel = t
        ? t("ritualDetail.buttons.buy", { price: ritual.price ?? 0 })
        : `Buy - $${ritual.price ?? 0}`
    } else if (isInvited) {
      // User was invited - show ACCEPT INVITE for both public and private free rituals
      actionLabel = t ? t("ritualDetail.buttons.acceptInvite") : "ACCEPT INVITE"
      buttonVariant = "primary"
    } else {
      const isInviteReq = ritual.whoCanJoin?.includes("invite_req") ?? false
      if (isInviteReq) {
        if (ritual.hasPendingRequest) {
          actionLabel = t ? t("ritualDetail.buttons.cancelRequest") : "CANCEL REQUEST"
        } else {
          actionLabel = t ? t("ritualDetail.buttons.requestToJoin") : "REQUEST TO JOIN"
        }
      } else {
        actionLabel = t ? t("ritualDetail.buttons.join") : "JOIN RITUAL"
      }
    }
  } else {
    // Joined - buttons vary by format
    showHamburger = true

    switch (ritual.format) {
      case "in-person":
      case "virtual-course":
        actionLabel = t ? t("ritualDetail.buttons.joined") : "JOINED"
        break
      case "self-guided":
        if (currentProgressStatus === "IN_PROGRESS") {
          // User is currently doing the ritual
          actionLabel = t ? t("ritualDetail.buttons.markAsDone") : "MARK AS DONE"
          buttonVariant = "primary"
        } else {
          // User can start a new ritual
          const label = t ? t("ritualDetail.buttons.startRitual") : "START RITUAL"
          actionLabel = completedDays > 0 ? `${label} (${completedDays})` : label
        }
        showStartButton = true
        break
      case "self-guided-course":
        if (showContinueCourse) {
          // User is actively doing the course - show "CONTINUE COURSE"
          actionLabel = t ? t("ritualDetail.buttons.continueCourse") : "CONTINUE COURSE"
        } else {
          // User can start or restart - show "START COURSE"
          actionLabel = t ? t("ritualDetail.buttons.startCourse") : "START COURSE"
          buttonVariant = "secondary"
        }
        showStartButton = true
        break
      default:
        actionLabel = t ? t("ritualDetail.buttons.joined") : "JOINED"
    }
  }

  return {
    showActionButton: !isCreator,
    actionLabel,
    showHamburger: showHamburger && !isCreator,
    showStartButton,
    buttonVariant,
    canAccessContent: canAccess ?? false,
    canStartRitual,
    isInProgress: currentProgressStatus === "IN_PROGRESS",
    completedDays,
    courseProgress,
    isInviteReq: ritual.whoCanJoin?.includes("invite_req") ?? false,
    hasPendingRequest: ritual.hasPendingRequest ?? false,
    isInvited,
    inviteId,
  }
}

export type RitualButtonState = ReturnType<typeof getRitualButtonState>

export function RitualDetailScreen() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [showReportModal, setShowReportModal] = useState(false)
  const [showShareDialog, setShowShareDialog] = useState(false)
  const [isViewingAsUser, setIsViewingAsUser] = useState(false)
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [showProfileModal, setShowProfileModal] = useState(false)
  const profileModalHandledRef = useRef(false)

  // Reset the handled guard when modal opens
  useEffect(() => {
    if (showProfileModal) {
      profileModalHandledRef.current = false
    }
  }, [showProfileModal])
  const queryClient = useQueryClient()

  // Deep-link landing: true when this ritual is the page a brand-new user was
  // sent to via a shared link (the welcome step marked it when honoring the
  // return-to). Read it once so the ritual tour does NOT auto-start and redirect
  // them off this ritual. The home tour still ensures on their next Home visit.
  // The marker is path-scoped, so it only matches when THIS ritual is the
  // deep-link target. (issue #565)
  const deepLinkLandingRef = useRef<boolean | null>(null)
  if (deepLinkLandingRef.current === null) {
    deepLinkLandingRef.current = consumeDeepLinkLanding()
  }

  // Tour state
  const tour = useTourInit("ritual_tour", {
    metadata: { ritualId: id ?? undefined },
    suppressEnsure: deepLinkLandingRef.current,
    onEnsure: async () => {
      const demoId = await getDemoRitualId()
      if (demoId) {
        window.location.href = `/rituals/${demoId}?tour=ritual&createdRitualId=${id}`
      }
    },
  })

  // Track which params have been processed to prevent duplicate toasts (e.g., from React StrictMode double-mount)
  const processedParamsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    const payment = searchParams.get("payment")
    const donated = searchParams.get("donated")
    const share = searchParams.get("share")

    if (payment === "success" && !processedParamsRef.current.has("payment=success")) {
      processedParamsRef.current.add("payment=success")
      toast.success("Payment successful! Your session has been booked.")
      setSearchParams({}, { replace: true })
    } else if (payment === "cancelled" && !processedParamsRef.current.has("payment=cancelled")) {
      processedParamsRef.current.add("payment=cancelled")
      toast.error("Payment was cancelled.")
      setSearchParams({}, { replace: true })
    } else if (donated === "true" && !processedParamsRef.current.has("donated=true")) {
      processedParamsRef.current.add("donated=true")
      toast.success("Thank you! Donation sent successfully.")
      queryClient.invalidateQueries({ queryKey: ["ritual", id, "members"] })
      setSearchParams({}, { replace: true })
    } else if (share === "true" && !processedParamsRef.current.has("share=true")) {
      processedParamsRef.current.add("share=true")
      setShowShareDialog(true)
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, setSearchParams, id, queryClient])

  const isMobile = useIsMobile()
  // Translations
  const { t } = useTranslation("rituals")

  // Get current user
  const user = useAuthStore(state => state.user)
  const isGuest = !user
  const [showLoginModal, setShowLoginModal] = useState(false)

  // Toggle save hook
  const toggleRitualSave = useToggleRitualSave()

  // Publish ritual mutation
  const publishMutation = useMutation({
    mutationFn: (ritualId: string) => publishRitual(ritualId),
    onSuccess: () => {
      toast.success("Ritual published successfully")
      refetch()
      queryClient.invalidateQueries({ queryKey: ["user-ritual-teachings", user?.id] })
    },
  })

  // Archive ritual mutation
  const archiveMutation = useMutation({
    mutationFn: (ritualId: string) => archiveRitual(ritualId),
    onSuccess: () => {
      toast.success("Ritual archived")
      refetch()
      queryClient.invalidateQueries({ queryKey: ["user-ritual-teachings", user?.id] })
    },
  })

  // Unarchive ritual mutation
  const unarchiveMutation = useMutation({
    mutationFn: (ritualId: string) => unarchiveRitual(ritualId),
    onSuccess: () => {
      toast.success("Ritual unarchived")
      refetch()
      queryClient.invalidateQueries({ queryKey: ["user-ritual-teachings", user?.id] })
    },
  })

  // Handle publish ritual
  const handlePublishRitual = () => {
    if (id) {
      publishMutation.mutate(id)
    }
  }

  // Fetch ritual data from API
  const {
    data: apiRitual,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["ritual", id],
    queryFn: () => (id ? getRitualById(id) : Promise.reject(new Error("No id provided"))),
    enabled: !!id,
  })

  useEffect(() => {
    if (!isLoading && location.hash === "#discussion") {
      const el = document.getElementById("discussion")
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100)
      }
    }
  }, [isLoading, location.hash])

  // Transform API data to UI format
  const ritualData: RitualDetailData | undefined = apiRitual
    ? transformRitualDetailToUI(apiRitual)
    : undefined

  // Effective isOwner - false when viewing as user (admin toggle)
  const effectiveIsOwner = (ritualData?.isOwner ?? false) && !isViewingAsUser
  const effectiveIsOrganizer = (ritualData?.isOrganizer ?? false) && !isViewingAsUser
  const effectiveCanEdit = (ritualData?.canEdit ?? false) && !isViewingAsUser
  const effectiveCreatedBy = !isViewingAsUser ? ritualData?.createdBy : undefined

  // Fetch ritual organizers with infinite scroll (includes isFollowing/isMember status)
  const { organizers: infiniteScrollOrganizers, pagination } = useRitualOrganizers({
    ritualId: id || "",
    status: "JOINED",
  })

  // Transform RitualOrganizerItem[] to RitualOrganizer[] for sidebar compatibility
  const transformedOrganizers: RitualOrganizer[] = useMemo(() => {
    const items = infiniteScrollOrganizers.map(org => ({
      id: org.id,
      organiserType: org.organiserType,
      organiserId: org.organiserId,
      user: org.user
        ? {
            ...org.user,
            isFollowing: org.user.isFollowing,
            followerCount: org.user.followerCount,
          }
        : null,
      community: org.community
        ? {
            id: org.community.id,
            name: org.community.name || "",
            handle: org.community.handle || "",
            coverImage: org.community.coverImage,
            isMember: org.community.isMember,
            totalMemberCount: org.community.totalMemberCount,
          }
        : null,
      name: org.user?.name || org.community?.name || "Unknown",
      avatar:
        org.user?.coverImage ||
        org.community?.coverImage ||
        org.user?.profilePictureUrl ||
        undefined,
      followers: 0,
    }))

    // For default/onboarding rituals with no real organizers, show Rituality
    if (
      items.length === 0 &&
      (ritualData?.metadata as Record<string, unknown> | null)?.isOnboardingDemo
    ) {
      const demoData = (ritualData?.metadata as Record<string, unknown> | null)?.demoData as
        | { organizers?: Array<{ name: string; handle: string; avatarAsset: string }> }
        | undefined
      if (demoData?.organizers?.[0]) {
        const org = demoData.organizers[0]
        items.push({
          id: "rituality-demo",
          organiserType: "user",
          organiserId: "",
          name: org.name,
          avatar: aiAvatarImg,
          followers: 2500,
          user: null,
          community: null,
        })
      }
    }

    return items
  }, [infiniteScrollOrganizers, ritualData])

  // Compute button state from API response
  const buttonState = useMemo(
    () =>
      ritualData
        ? getRitualButtonState(ritualData, user?.id, t)
        : getRitualButtonState({} as RitualDetailData, user?.id, t),
    [ritualData, user?.id, t],
  )

  // For default/onboarding demo rituals: grant content access (no blur)
  const isOnboardingDemo = (ritualData?.metadata as Record<string, unknown> | null)
    ?.isOnboardingDemo as boolean | undefined
  const effectiveButtonState = useMemo(() => {
    let state = isOnboardingDemo ? { ...buttonState, canAccessContent: true } : { ...buttonState }
    if (tour.showOverlay) {
      if (tour.currentStep === 5) {
        // Screen 5: show "Mark as done" with olive background
        state = {
          ...state,
          showActionButton: true,
          showHamburger: false,
          showStartButton: false,
          actionLabel: "MARK AS DONE",
          buttonVariant: "primary",
          isInProgress: true,
        }
      } else {
        // All other steps: show "Start Ritual"
        state = {
          ...state,
          showActionButton: true,
          showHamburger: false,
          showStartButton: true,
          actionLabel: "START RITUAL",
        }
      }
    }
    return state
  }, [buttonState, isOnboardingDemo, tour.showOverlay, tour.currentStep])

  // Use isSaved directly from query data - React Query will keep it in sync
  const isSaved = ritualData?.isSaved ?? false

  // Handle bookmark toggle
  const handleBookmarkToggle = () => {
    toggleRitualSave.mutate(id || "")
  }

  // Handle report ritual
  const handleReportRitual = () => {
    setShowReportModal(true)
  }

  const hasEditAccess = effectiveCanEdit

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-stone">{t("ritualDetail.loading")}</p>
      </div>
    )
  }

  // Error state
  if (isError || !ritualData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-red">{t("ritualDetail.error")}</p>
      </div>
    )
  }

  // Display data - use effective values when viewing as user
  const displayData: RitualDetailData = {
    ...ritualData,
    isOwner: effectiveIsOwner,
    isOrganizer: effectiveIsOrganizer,
    canEdit: effectiveCanEdit,
    createdBy: effectiveCreatedBy,
    // Show "Mark as Done" state when tour is on step 5
    currentProgressStatus:
      tour.showOverlay && tour.currentStep === 5
        ? "IN_PROGRESS"
        : ritualData?.currentProgressStatus,
    // Override participant stats for onboarding demo rituals
    participantStats: (ritualData?.metadata as Record<string, unknown> | null)?.isOnboardingDemo
      ? {
          joined: 217,
          saved: 45,
          avatars:
            (ritualData?.sampleMembers
              ?.map(m => m.profileCoverImage)
              .filter(Boolean) as string[]) || [],
        }
      : ritualData?.participantStats,
  }

  const hasSteps = ritualData.stepByStepGuidance && ritualData.stepByStepGuidance.length > 0
  const hasCourseDays = ritualData.courseDays && ritualData.courseDays.length > 0

  const rawDiscussionPermissions = ritualData.discussionPermissions || {
    canComment: false,
    canReply: false,
  }
  // When viewing as user, check if "everyone" can comment - if so, allow commenting
  const anyoneCanComment = ritualData.whoCanComment?.includes("everyone") ?? false
  const discussionPermissions = isViewingAsUser
    ? { canComment: anyoneCanComment, canReply: anyoneCanComment }
    : rawDiscussionPermissions

  // Admin bar handlers
  const handleEditRitual = () => {
    navigate(`/rituals/edit/${id}`)
  }

  const handleViewAsUser = () => {
    setIsViewingAsUser(!isViewingAsUser)
  }

  const handleStatusChange = async (action: "complete" | "cancel" | "publish") => {
    if (!id) return

    if (action === "publish") {
      handlePublishRitual()
    } else if (action === "cancel") {
      setShowCancelDialog(true)
    } else if (action === "complete") {
      try {
        await adminCompleteRitual(id)
        toast.success("Ritual marked as completed!")
        queryClient.invalidateQueries({ queryKey: ["ritual", id] })
      } catch (error) {
        console.error("Failed to complete ritual:", error)
        toast.error("Failed to complete ritual")
      }
    }
  }

  return (
    <div className={`w-full md:mt-5 max-w-360 mx-auto${ritualData.isOwner ? " pb-24" : ""}`}>
      {/* Media Carousel with Back Button and Action Buttons Overlay */}
      <div className="relative">
        {/* Back Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="hidden md:flex absolute top-5.5 p-5 left-17.5 z-20 rounded-md border-gradient-glass hover:border-gradient-glass"
        >
          <ArrowLeftIcon size="5" color="white" />
        </Button>

        {/* Action Buttons - Top Right */}
        <div className="hidden md:flex items-center gap-2.5 absolute top-5 right-17 z-20">
          {hasEditAccess && (
            <Button
              variant="ghost"
              size="sm"
              className="rounded-md border-gradient-glass h-11 w-11"
              onClick={() => navigate(`/rituals/edit/${id}`)}
              aria-label={t("ritualHeader.editRitual")}
            >
              <Pencil className="!w-5 !h-5 text-white" />
            </Button>
          )}
          {isGuest ? (
            <GuestAction>
              <Button
                variant="ghost"
                size="sm"
                className="border-gradient-glass rounded-md h-11 w-11"
                aria-label={
                  isSaved ? t("ritualHeader.removeBookmark") : t("ritualHeader.addBookmark")
                }
              >
                <Bookmark
                  className={`!w-5 !h-5 ${isSaved ? "fill-current text-white" : "text-white"}`}
                />
              </Button>
            </GuestAction>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="border-gradient-glass rounded-md h-11 w-11"
              onClick={handleBookmarkToggle}
              disabled={toggleRitualSave.isPending}
              aria-label={
                isSaved ? t("ritualHeader.removeBookmark") : t("ritualHeader.addBookmark")
              }
            >
              <Bookmark
                className={`!w-5 !h-5 ${isSaved ? "fill-current text-white" : "text-white"}`}
              />
            </Button>
          )}
          {ritualData.accessType !== "PERSONAL" && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowShareDialog(true)}
              aria-label={t("ritualHeader.shareRitual")}
              className="rounded-md border-gradient-glass h-11 w-11"
            >
              <Share className="!w-5 !h-5 text-white " />
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                aria-label={t("ritualHeader.moreOptions")}
                className="rounded-md border-gradient-glass h-11 w-11"
              >
                <EllipsisVertical className="!w-5 !h-5 text-white" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-lg p-3">
              {!ritualData.isLive && (
                <DropdownMenuItem
                  onClick={handlePublishRitual}
                  disabled={publishMutation.isPending}
                  className="cursor-pointer"
                >
                  {publishMutation.isPending ? "Publishing..." : "Publish"}
                </DropdownMenuItem>
              )}
              {effectiveIsOwner && !ritualData.isArchived && (
                <DropdownMenuItem
                  onClick={() => {
                    if (id) archiveMutation.mutate(id)
                  }}
                  disabled={archiveMutation.isPending}
                  className="cursor-pointer"
                >
                  <Archive className="mr-2 size-4" />
                  {archiveMutation.isPending ? "Archiving..." : "Archive"}
                </DropdownMenuItem>
              )}
              {effectiveIsOwner && ritualData.isArchived && (
                <DropdownMenuItem
                  onClick={() => {
                    if (id) unarchiveMutation.mutate(id)
                  }}
                  disabled={unarchiveMutation.isPending}
                  className="cursor-pointer"
                >
                  <ArchiveRestore className="mr-2 size-4" />
                  {unarchiveMutation.isPending ? "Unarchiving..." : "Unarchive"}
                </DropdownMenuItem>
              )}
              {isGuest ? (
                <DropdownMenuItem
                  onClick={() => setShowLoginModal(true)}
                  className="text-brand-terracotta cursor-pointer"
                >
                  <Flag className="mr-2 size-4" />
                  {t("ritualHeader.report")}
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={handleReportRitual}
                  className="text-brand-terracotta cursor-pointer"
                >
                  <Flag className="mr-2 size-4" />
                  {t("ritualHeader.report")}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Media Carousel */}
        <RitualMediaCarousel
          media={ritualData.media}
          ritualId={ritualData.id}
          requiresSignedToken={ritualData.accessType !== "PUBLIC"}
          className="min-[1400px]:rounded-[30px]"
          isGuest={isGuest}
          canAccessContent={effectiveButtonState.canAccessContent}
          onGuestMediaPlay={() => {
            if (isGuest) {
              setShowLoginModal(true)
            } else {
              toast.info(
                t(
                  "ritualDetail.pleaseJoinToPlayMedia",
                  "Please join the ritual to play this video/audio",
                ),
              )
            }
          }}
        />
      </div>

      {/* Main Content - 2 Column Layout */}
      <div className="py-7.5 md:py-10 px-5 md:px-17.5">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-7.5">
          {/* Left Column */}
          <div className="lg:col-span-8 space-y-7.5">
            {/* Title and Description */}
            <RitualInfoSection
              title={ritualData.title}
              description={ritualData.description}
              ritualData={ritualData}
              buttonState={effectiveButtonState}
              onActionSuccess={() => refetch()}
              isGuest={isGuest}
            />
            <div className="space-y-5">
              {/* Accordion Sections - Hide for PERSONAL visibility */}
              <div id="tour-ritual-content" className="space-y-5">
                {ritualData.visibility !== "PERSONAL" && (
                  <>
                    <WhatYoullNeedSection
                      items={ritualData.whatYoullNeed ?? []}
                      defaultOpen={!isMobile}
                    />

                    <WhatYoullExperienceSection
                      items={ritualData.whatYoullExperience ?? []}
                      defaultOpen={!isMobile}
                    />
                  </>
                )}

                {/* Step-by-Step Guidance (self-guided rituals) */}
                {hasSteps && ritualData.stepByStepGuidance && (
                  <StepByStepGuidanceSection
                    steps={ritualData.stepByStepGuidance}
                    canAccess={effectiveButtonState.canAccessContent}
                    defaultOpen={!isMobile}
                  />
                )}
              </div>

              {/* Course Days (self-guided courses) */}
              {hasCourseDays && ritualData.courseDays && (
                <RitualCourseDays days={ritualData.courseDays} defaultOpen={!isMobile} />
              )}

              {/* Course Modules (self-guided courses with video modules). */}
              {ritualData.courseModules && ritualData.courseModules.length > 0 && (
                <CourseModulesList
                  modules={ritualData.courseModules}
                  audioCoverImageUrl={
                    ritualData.media?.find(m => m.type === "image" && m.isCover)?.url ||
                    ritualData.media?.find(m => m.type === "image")?.url
                  }
                  ritualId={id}
                  canAccessContent={effectiveButtonState.canAccessContent}
                  courseVideoAccess={ritualData.courseVideoAccess}
                />
              )}

              {/* Intention */}
              {(ritualData.intentions?.length || ritualData.intention) && (
                <IntentionSection
                  intention={ritualData.intention}
                  intentions={ritualData.intentions}
                  defaultOpen={!isMobile}
                />
              )}
            </div>

            {/* Tags */}
            <RitualTagsList tags={ritualData.tags} />

            {!isMobile && (
              <DiscussionSection
                entityId={ritualData.id}
                canComment={discussionPermissions.canComment}
                canReply={discussionPermissions.canReply}
                canModerate={Boolean(effectiveIsOrganizer || effectiveCreatedBy === user?.id)}
                canNotifyMembers={Boolean(effectiveIsOrganizer || effectiveCreatedBy === user?.id)}
                isGuest={isGuest}
              />
            )}
          </div>

          {/* Right Column - Sidebar */}
          <div className="lg:col-span-4" id="tour-ritual-sidebar">
            <RitualSidebar
              data={displayData}
              buttonState={effectiveButtonState}
              onActionSuccess={() => refetch()}
              onShare={() => setShowShareDialog(true)}
              organizers={transformedOrganizers}
              totalOrganizers={
                transformedOrganizers.length > 0 &&
                (ritualData?.metadata as Record<string, unknown> | null)?.isOnboardingDemo
                  ? transformedOrganizers.length
                  : pagination?.totalItems
              }
              isGuest={isGuest}
            />
          </div>
        </div>
      </div>
      {isMobile && (
        <div className="px-5 ">
          <DiscussionSection
            entityId={ritualData.id}
            canComment={discussionPermissions.canComment}
            canReply={discussionPermissions.canReply}
            canModerate={Boolean(effectiveIsOrganizer || effectiveCreatedBy === user?.id)}
            canNotifyMembers={Boolean(effectiveIsOrganizer || effectiveCreatedBy === user?.id)}
            isGuest={isGuest}
          />
        </div>
      )}

      {/* Explore More — hidden for onboarding demo rituals */}
      {!(ritualData?.metadata as Record<string, unknown> | null)?.isOnboardingDemo && (
        <ExploreMoreSection currentRitualId={id || ""} />
      )}

      {/* Report Modal */}
      {ritualData && (
        <ReportModal
          open={showReportModal}
          onOpenChange={setShowReportModal}
          target="RITUAL"
          targetData={{
            id: ritualData.id,
            name: ritualData.title,
            handle: ritualData.organizers?.[0]?.user?.username || undefined,
            imageUrl:
              ritualData.media?.find(m => m.isCover)?.url || ritualData.media?.[0]?.url || null,
          }}
        />
      )}

      {/* Share to Social Dialog - hidden for PERSONAL rituals */}
      {ritualData && ritualData.accessType !== "PERSONAL" && (
        <ShareModal
          open={showShareDialog}
          onOpenChange={setShowShareDialog}
          shareUrl={`/rituals/${ritualData.slug || ritualData.id}`}
          shareText={ritualData.title}
          shareType="ritual"
          shareImageUrl={
            ritualData.media?.find(m => m.isCover)?.url ||
            ritualData.media?.find(m => m.type === "image")?.url ||
            null
          }
          displayPath={`/rituals/${ritualData.title.toLowerCase().replace(/\s+/g, "-")}`}
        />
      )}

      {/* Admin Bar - Only visible to ritual creators */}
      {ritualData.isOwner && (
        <RitualDetailAdminBar
          status={
            ritualData.canceledAt
              ? "canceled"
              : ritualData.isCompleted
                ? "completed"
                : !ritualData.isLive
                  ? "draft"
                  : "active"
          }
          updatedAt={ritualData.updatedAt}
          updatedBy={ritualData.updatedBy}
          isViewingAsUser={isViewingAsUser}
          isPublishing={publishMutation.isPending}
          onEdit={handleEditRitual}
          onViewAsUser={handleViewAsUser}
          onStatusChange={handleStatusChange}
        />
      )}

      {/* Cancel Ritual Dialog */}
      <CancelRitualDialog
        isOpen={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        ritualId={id || ""}
        ritualTitle={ritualData.title || ""}
      />

      <LoginModal open={showLoginModal} onOpenChange={setShowLoginModal} />

      {/* Ritual Tour Overlay */}
      {tour.showOverlay && (
        <RitualTourOverlay
          currentStep={tour.currentStep}
          onNext={tour.goNext}
          onBack={tour.goBack}
          onSkip={async () => {
            await tour.dismissTour()
            const redirectTo = tour.createdRitualId
            if (redirectTo) {
              navigate(`/rituals/${redirectTo}`, { replace: true })
            } else {
              const status = await getTourStatus("home_tour")
              if (!status.exists) {
                const { queueTour } = await import("@/features/tour/services/tour.service")
                await queueTour("home_tour", "on_home_visit").catch(() => {})
              }
              navigate("/", { replace: true })
            }
          }}
          onDone={async () => {
            await tour.finishTour()
            const redirectTo = tour.createdRitualId
            if (redirectTo) {
              navigate(`/rituals/${redirectTo}`, { replace: true })
              return
            }
            // Skip profile modal if the user already has a complete profile
            if (user && isProfileFullyCompleted(user)) {
              getTourStatus("home_tour")
                .then(async status => {
                  if (!status.exists) {
                    const { queueTour } = await import("@/features/tour/services/tour.service")
                    await queueTour("home_tour", "on_home_visit").catch(() => {})
                  }
                })
                .catch(() => {})
              navigate("/", { replace: true })
              return
            }
            setShowProfileModal(true)
          }}
        />
      )}

      {/* Profile modal — shown after tour completion, outside overlay */}
      {showProfileModal && (
        <ProfileIncompleteModal
          isOpen={showProfileModal}
          user={user}
          closeOnly
          onClose={(wasItemClick?: boolean) => {
            // Guard against double-invocation: handleItemClick calls onClose,
            // then the Dialog's onOpenChange fires handleGoBack → onClose again.
            if (profileModalHandledRef.current) return
            profileModalHandledRef.current = true
            setShowProfileModal(false)
            // Queue the home tour only if the user hasn't already had one
            // (e.g., completed it earlier in the onboarding flow). Otherwise
            // the dashboard would auto-start a duplicate tour.
            getTourStatus("home_tour")
              .then(async status => {
                if (!status.exists) {
                  const { queueTour } = await import("@/features/tour/services/tour.service")
                  await queueTour("home_tour", "on_home_visit").catch(() => {})
                }
              })
              .finally(() => {
                if (!wasItemClick) {
                  navigate("/")
                }
              })
          }}
        />
      )}
    </div>
  )
}
